import { useState, useEffect, useRef, useMemo } from 'react'
import { Container, Col, Row, Badge } from 'reactstrap'
import { useSelector, useDispatch } from 'react-redux'
import { getAllAnalyzers } from '../../redux/slices'
import './Analyzers.css'
import ComponentBox from '../../components/ComponentBox/ComponentBox'
import { useTimezone } from '../../hooks/useTimezone'
import { ICSModal, TanStackDatagrid, TanStackDatagridWithExport } from '../../components'
import {
  TanStackDatagridColumn,
  TanStackDatagridFilter,
  TanStackDatagridPagination,
  TanStackDatagridRowAction,
  TanStackDatagridSort
} from '../../components/TanStackDataGrid/TanStackDatagrid'
// import RenderDataModal from '../Data/Modals/RenderViewModal/RenderViewModal'
import {
  analyzersAdapter,
  removeAnalyzer,
  removeAnalyzerResults,
  setEntityError
} from '../../redux/slices'
import { DefaultState } from '../../types'
import HeaderBar from '../../components/HeaderBar/HeaderBar'
import { ICSDataCellDetailView } from '../../components/ICSDataCellDetailView/ICSDataCellDetailView'
import { capitalizeFirstLetter } from '../../utils/helpers'
import ICSActionButton from '../../components/ICS/ActionButton/ICSActionButton'
import ICSErrorCard from '../../components/ICSErrorCard/ICSErrorCard'
import { AppDispatch, NetworkRequestError } from '../../services/lib'
import AnalyzerFormWrapper from './AnalyzerGenerator/AnalyzerFormWrapper'
import { postIpaManual } from '../../services/endpoints/jobQueue'
import { showToast } from '../../utils/types'

const analyzerSelectors = analyzersAdapter.getSelectors<DefaultState>(
  (state) => state.analyzers
)

interface AnalyzerRow {
  id: string | number
  name?: string
  description?: string
  type?: string
  script?: { name?: string }
  configs?: Array<{ name?: string }>
  datasets?: Array<{ name?: string }>
  last_status?: string
  user?: { id?: string | number; name?: string }
  created_at?: string
  updated_at?: string
}

const badgeColorMap = {
  status: {
    new: 'secondary',
    running: 'primary',
    complete: 'success',
    failed: 'danger'
  },
  dataSet: {
    static: 'dark',
    dynamic: 'warning'
  }
}

const LastStatusBadge = (data: { value: string }) => {
  return (
    <Badge color={badgeColorMap.status[data.value] || 'primary'} pill>
      {data.value && capitalizeFirstLetter(data.value)}
    </Badge>
  )
}

const DataSetBadge = (data: { value: string }) => {
  return (
    <Badge color="info" pill>
      {data.value && capitalizeFirstLetter(data.value)}
    </Badge>
  )
}

const Analyzers = () => {
  const { formatLocalTimeFromUtc } = useTimezone()
  const dispatch: AppDispatch = useDispatch()
  const analyzersError = useSelector(
    (state: DefaultState) => state.analyzers.error
  )
  const analyzersArray = useSelector(analyzerSelectors.selectAll) as AnalyzerRow[]
  const [showGenerateModal, setShowGenerateModal] = useState<boolean>(false)
  const [showEditModal, setShowEditModal] = useState<boolean>(false)
  const [editAnalyzer, setEditAnalyzer] = useState<AnalyzerRow | null>(null)
  const interval: { current: NodeJS.Timeout | null } = useRef(null)
  const [sort, setSort] = useState<TanStackDatagridSort | null>(null)
  const [filters, setFilters] = useState<TanStackDatagridFilter[]>([])
  const [pagination, setPagination] = useState<TanStackDatagridPagination>({
    pageIndex: 0,
    pageSize: 10
  })
  const [columnVisibility, setColumnVisibility] = useState<
    Record<string, boolean>
  >({})

  const enqueueManualRun = async (
    analyzerId: string | number,
    analyzerName?: string
  ) => {
    dispatch(
      setEntityError({
        sliceName: 'analyzers',
        entityName: 'Analyzer',
        error: null
      })
    )

    try {
      await postIpaManual(String(analyzerId))
      const successMessage = analyzerName
        ? `Analyzer "${analyzerName}" run queued`
        : 'Analyzer run queued'
      showToast(successMessage, 'success', 2500)
      return true
    } catch (error) {
      const networkError = error as NetworkRequestError
      const errorMessage =
        networkError?.message || 'Failed to queue analyzer run.'

      dispatch(
        setEntityError({
          sliceName: 'analyzers',
          entityName: 'Analyzer',
          error: {
            code: networkError?.code || 'error',
            message: errorMessage,
            fields: networkError?.fields || []
          }
        })
      )
      showToast(errorMessage, 'error', 4000)
      return false
    }
  }

  useEffect(() => {
    const initialState = async () => {
      await dispatch(getAllAnalyzers())
    }

    if (showGenerateModal || showEditModal) {
      clearInterval(interval.current as NodeJS.Timeout)
    } else {
      interval.current = setInterval(async () => {
        // eslint-disable-next-line no-console
        initialState()
      }, 10000)
    }
  }, [dispatch, showGenerateModal, showEditModal])

  useEffect(() => {
    dispatch(getAllAnalyzers())
  }, [])

  const analyzerGenerateButtonClicked = () => {
    setShowGenerateModal(true)
  }
  const hideEditModal = () => {
    setShowEditModal(false)
    dispatch(
      setEntityError({
        sliceName: 'analyzers',
        entityName: 'Analyzer',
        error: null
      })
    )
  }
  const hideModal = () => {
    setShowGenerateModal(false)
    dispatch(
      setEntityError({
        sliceName: 'analyzers',
        entityName: 'Analyzer',
        error: null
      })
    )
  }
  const handleEdit = (data: AnalyzerRow) => {
    if (data) {
      setEditAnalyzer(data)
      setShowEditModal(true)
    }
  }
  const handleRun = async (data: AnalyzerRow) => {
    if (data) {
      const r = window.confirm(
        `Are you sure you want to run analyzer (${data.name})?`
      )
      if (r === true) {
        await enqueueManualRun(data.id, data.name)
      }
    }
  }
  const handleDelete = (data: AnalyzerRow) => {
    if (data) {
      const r = window.confirm(
        `Are you sure you want to delete the following Analyzer: ${data.name}?`
      )
      if (r === true) {
        dispatch(removeAnalyzer(String(data.id)))
      }
    }
  }
  const handleDeleteResults = (data: AnalyzerRow) => {
    if (data) {
      const r = window.confirm(
        `Are you sure you want to delete ALL results currently assigned to Analyzer: ${data.name}`
      )
      if (r === true) {
        dispatch(removeAnalyzerResults(String(data.id)))
      }
    }
  }
  const checkEnabled = (data: AnalyzerRow) => {
    if (!data || !data.user || !data.user.id) return false
    const enabled =
      typeof data.last_status === 'string' && data.last_status === 'running'
        ? false
        : true
    return enabled
  }

  const data = useMemo(() => analyzersArray ?? [], [analyzersArray])

  const columns = useMemo<TanStackDatagridColumn<AnalyzerRow>[]>(() => [
    {
      id: 'name',
      header: 'Name',
      accessor: (analyzer) => analyzer.name || '',
      sortable: true,
      filterable: true,
      resizable: true,
      hideable: true
    },
    {
      id: 'description',
      header: 'Description',
      accessor: (analyzer) => analyzer.description || '',
      cell: (analyzer) => (
        <ICSDataCellDetailView
          value={analyzer.description || ''}
          data={analyzer}
          row={{ data: analyzer }}
          modalWidth="35vw"
          modalHeight="20vh"
        />
      ),
      className: 'td-dataset-description',
      sortable: true,
      filterable: true,
      resizable: true,
      hideable: true
    },
    {
      id: 'type',
      header: 'Type',
      accessor: (analyzer) => analyzer.type || '',
      sortable: true,
      filterable: true,
      resizable: true,
      hideable: true
    },
    {
      id: 'scriptName',
      header: 'Script Name',
      accessor: (analyzer) => analyzer.script?.name || '',
      cell: (analyzer) => {
        const scriptName = analyzer.script?.name
        if (!scriptName)
          return <span className="text-danger">Script is missing</span>
        return <span>{scriptName}</span>
      },
      sortable: true,
      filterable: true,
      resizable: true,
      hideable: true
    },
    {
      id: 'configName',
      header: 'Configuration',
      accessor: (analyzer) => analyzer.configs?.[0]?.name || '',
      cell: (analyzer) => {
        const configName = analyzer.configs?.[0]?.name
        if (!configName)
          return <span className="text-danger">Config is missing</span>
        return <span>{configName}</span>
      },
      sortable: true,
      filterable: true,
      resizable: true,
      hideable: true
    },
    {
      id: 'datasetName',
      header: 'Data Sets',
      accessor: (analyzer) => analyzer.datasets?.[0]?.name || '',
      cell: (analyzer) => (
        <DataSetBadge value={analyzer.datasets?.[0]?.name || ''} />
      ),
      sortable: true,
      filterable: true,
      resizable: true,
      hideable: true
    },
    {
      id: 'lastStatus',
      header: 'Last Status',
      accessor: (analyzer) => analyzer.last_status || '',
      cell: (analyzer) => {
        if (analyzer.type === 'ingress') return null
        return <LastStatusBadge value={analyzer.last_status || ''} />
      },
      sortable: true,
      filterable: true,
      resizable: true,
      hideable: true
    },
    {
      id: 'userName',
      header: 'User',
      accessor: (analyzer) => analyzer.user?.name || '',
      sortable: true,
      filterable: true,
      resizable: true,
      hideable: true
    },
    {
      id: 'created_at',
      header: 'Date Created',
      accessor: (analyzer) => analyzer.created_at || '',
      cell: (analyzer) => formatLocalTimeFromUtc(analyzer.created_at) ?? '',
      sortable: true,
      filterable: true,
      resizable: true,
      hideable: true
    },
    {
      id: 'updated_at',
      header: 'Last Updated',
      accessor: (analyzer) => analyzer.updated_at || '',
      cell: (analyzer) => formatLocalTimeFromUtc(analyzer.updated_at) ?? '',
      sortable: true,
      filterable: true,
      resizable: true,
      hideable: true
    }
  ], [formatLocalTimeFromUtc])

  const actions = useMemo<TanStackDatagridRowAction<AnalyzerRow>[]>(() => [
    {
      id: 'edit',
      icon: 'lucide#pencil',
      label: 'Edit',
      disabled: (analyzer) => !checkEnabled(analyzer),
      onClick: handleEdit
    },
    {
      id: 'run',
      icon: 'lucide#play',
      label: 'Run',
      hidden: (analyzer) => analyzer.type === 'ingress',
      disabled: (analyzer) => !checkEnabled(analyzer),
      onClick: handleRun
    },
    {
      id: 'deleteResults',
      icon: 'lucide#eraser',
      label: 'Delete All Results',
      hidden: (analyzer) => analyzer.type === 'ingress',
      disabled: (analyzer) => !checkEnabled(analyzer),
      onClick: handleDeleteResults
    },
    {
      id: 'delete',
      icon: 'lucide#trash',
      label: 'Delete',
      disabled: (analyzer) => !checkEnabled(analyzer),
      onClick: handleDelete
    }
  ], [])

  return (
    <>
      <Container fluid={true}>
        <>
          {showGenerateModal && (
            <ICSModal
              visible={showGenerateModal}
              onHiding={hideModal}
              title="Create New Analyzer"
              position="center"
              id="analyzerModal"
            >
              <AnalyzerFormWrapper
                isEdit={false}
                onSuccess={async (createdAnalyzer) => {
                  if (
                    createdAnalyzer &&
                    createdAnalyzer.type === 'manual' &&
                    window.confirm(
                      `Run newly created analyzer (${createdAnalyzer.name})?`
                    )
                  ) {
                    await enqueueManualRun(
                      createdAnalyzer.id,
                      createdAnalyzer.name
                    )
                  }
                  hideModal()
                }}
              />
            </ICSModal>
          )}
        </>
        <>
          {showEditModal && editAnalyzer && (
            <ICSModal
              visible={showEditModal}
              onHiding={hideEditModal}
              title="Edit Analyzer"
              position="center"
              id="analyzerEditModal"
            >
              <AnalyzerFormWrapper
                isEdit={true}
                selectedAnalyzer={editAnalyzer}
                onSuccess={async (updatedAnalyzer) => {
                  if (
                    updatedAnalyzer &&
                    updatedAnalyzer.type === 'manual' &&
                    window.confirm(
                      `Run updated analyzer (${updatedAnalyzer.name})?`
                    )
                  ) {
                    await enqueueManualRun(
                      updatedAnalyzer.id,
                      updatedAnalyzer.name
                    )
                  }
                  hideEditModal()
                }}
              />
            </ICSModal>
          )}
        </>
        <Row>
          <Col className="wivi-col">
            <HeaderBar
              origin="IPA"
              routes={[
                {
                  title: 'Analyzers'
                }
              ]}
              help={{
                title: 'Analyzers',
                text: (
                  <>
                    <p>
                      Analyzers are the primary component of IPA and consist of
                      many of the other components. Analyzers are easy to
                      generate and edit and belong to the User who created them
                      specifically. To create one, simply navigate to IPA &gt;
                      Analyzer in the nav bar, and click the “Generate” button
                      which will prompt you to provide the components of an
                      Analyzer.{' '}
                    </p>

                    <p>
                      Analyzer’s necessarily require a Name, a Run Type ( either
                      Dynamic or Static ), an Analysis Script, and a Dataset. An
                      Analyzer also optionally includes Configurations, and
                      Users with whom you’d wish to automatically share the
                      results of the analysis with upon completion. Multiple
                      Analysis Scripts, Datasets, Configurations, and Users may
                      be applied to a given Analyzer.
                    </p>

                    <p>
                      When an Analyzer has been created you will see it on the
                      grid on the Analyzer page. To run a static Analyzer simply
                      click the run icon on the left of the grid. This queues a
                      manual run request that the IPA Scanner picks up and
                      processes. Processing in this case means a container will
                      be launched with all the necessary information, then the
                      selected Analysis script will run with any optional
                      configurations on the selected data set(s), and the
                      results will be outputted to the Results page. If there
                      were any issues with the container or the Python script
                      during processing, you will see a DockerLog.txt file
                      appear in the results which contains the error message.{' '}
                    </p>

                    <p>
                      {' '}
                      To activate a Dynamic dataset you simply click the
                      “active” check mark on the grid. The check mark box will
                      be green when it is active. When a dynamic Dataset is
                      active it means all new uploaded collections will be
                      checked against the dataset criteria, and if they match,
                      the analysis will be run.{' '}
                    </p>

                    <p>
                      {' '}
                      You can edit or delete Analyzers, using the corresponding
                      grid buttons, as you see fit as all Analyzers are unique
                      to the person who created them.{' '}
                    </p>
                  </>
                )
              }}
            />
          </Col>
        </Row>
        <Row>
          <Col xs={{ size: 12 }} className="wivi-col">
            <ComponentBox>
              {analyzersError?.code === 'error' && !showGenerateModal ? (
                <ICSErrorCard
                  code={analyzersError.code}
                  message={analyzersError.message}
                  fields={analyzersError.fields}
                />
              ) : (
                <div className="d-flex flex-column h-100">
                  <div className="flex-grow-1 min-vh-0">
                    <TanStackDatagridWithExport
                      data={data}
                      columns={columns}
                      actions={actions}
                      getRowId={(analyzer) => String(analyzer.id)}
                      toolbarContent={(
                        <ICSActionButton
                          id="analyzerGenerate"
                          type="success"
                          width={65}
                          height={30}
                          stylingMode="contained"
                          onClick={analyzerGenerateButtonClicked}
                          text="Add"
                        />
                      )}
                      sort={sort}
                      onSortChange={setSort}
                      filters={filters}
                      onFilterChange={setFilters}
                      enablePagination
                      pagination={pagination}
                      onPaginationChange={setPagination}
                      columnVisibility={columnVisibility}
                      onColumnVisibilityChange={setColumnVisibility}
                    />
                  </div>
                </div>
              )}
            </ComponentBox>
          </Col>
        </Row>
      </Container>
    </>
  )
}

export default Analyzers
